import type { ReactNode } from "react";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { Navigate, useLocation, useParams } from "src/router";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { useLifecycleStudioEnabled } from "src/hooks/use-lifecycle-studio-enabled";
import { useIsAdStudioEnabled } from "src/pages/agents/hooks/use-is-ad-studio-enabled";
import { useIsContextHubEnabled } from "src/pages/context-hub/use-is-context-hub-enabled";
import { lazyNamed } from "src/utils/lazy-named";
import { OpportunitiesFeatureRoute } from "src/pages/ad-studio/components/opportunities-feature-route";
import { AdStudioBrandProfileFilterProvider } from "src/pages/ad-studio/brand-profile-filter";
import { StickySearchParamsProvider } from "src/pages/ad-studio/sticky-search-params";
import { insightsSettingsPath } from "src/pages/ad-studio/insights/settings/insights-settings-paths";

import { GuardrailsIndexRedirect } from "src/pages/context-hub/guardrails/redirect";

import { ASSET_LIBRARY_PATH, CATALOG_PATH } from "./assets-paths";
import {
  CatalogIndexRedirect,
  CatalogProductRedirect,
} from "./catalog/catalog-redirects";
import { CONNECTIONS_PATH } from "./connections/paths";
import { BRAND_SETTINGS_SEGMENT, BRANDS_PATH } from "./brand-tabs";
import {
  DEFINITIONS_PATH,
  definitionPanePath,
  MEMORIES_PATH,
} from "./definitions/utils";
import { useDefinitionsKnowledgeRefreshEnabled } from "./definitions/use-definitions-knowledge-refresh-enabled";
import { useDefinitionsTabEnabled } from "./definitions/use-definitions-tab-enabled";
import { useExternalContextEnabled } from "./external-context-sources/use-external-context-enabled";

const ContextHubBrands = lazyNamed(
  () => import("./brands"),
  "ContextHubBrands",
);
const ContextHubLayout = lazyNamed(
  () => import("./context-agent/context-hub-layout"),
  "ContextHubLayout",
);
const contextHubContextImport = () => import("./context");
const ContextHubContext = lazyNamed(
  contextHubContextImport,
  "ContextHubContext",
);

export const preloads: RoutePreload[] = [
  ["/context-hub", contextHubContextImport],
];
const ContextHubConnectedData = lazyNamed(
  () => import("./connected-data"),
  "ContextHubConnectedData",
);
const ConnectedDataIntegrations = lazyNamed(
  () => import("./connected-data/integrations"),
  "ConnectedDataIntegrations",
);
const ConnectionsListPage = lazyNamed(
  () => import("./connections/list-page"),
  "ConnectionsListPage",
);
const ConnectionStatusPage = lazyNamed(
  () => import("./connections/status-page"),
  "ConnectionStatusPage",
);
const ConnectionFlowPage = lazyNamed(
  () => import("./connections/connection-flow"),
  "ConnectionFlowPage",
);
const ContextEntitiesSettings = lazyNamed(
  () => import("./context-entities"),
  "ContextEntitiesSettings",
);
const DagBuilderPage = lazyNamed(
  () => import("src/pages/dag-skills/dag-builder-page/dag-builder-page"),
  "DagBuilderPage",
);
const SkillsSettings = lazyNamed(
  () => import("./context-entities/skills"),
  "SkillsSettings",
);
const FileDetailPage = lazyNamed(
  () => import("./context-entities/detail"),
  "FileDetailPage",
);
const NewFilePage = lazyNamed(
  () => import("./context-entities/detail"),
  "NewFilePage",
);
const NewSkillPage = lazyNamed(
  () => import("./context-entities/detail"),
  "NewSkillPage",
);
const SkillDetailPage = lazyNamed(
  () => import("./context-entities/detail"),
  "SkillDetailPage",
);
const CopyReferencesPage = lazyNamed(
  () => import("./copy-references"),
  "CopyReferencesPage",
);
const CopyReferencesDetailPage = lazyNamed(
  () => import("./copy-references/detail"),
  "CopyReferencesDetailPage",
);
const DefinitionsPage = lazyNamed(
  () => import("./definitions"),
  "DefinitionsPage",
);
const DefinitionsListPage = lazyNamed(
  () => import("./definitions/definitions-list-page"),
  "DefinitionsListPage",
);
const DefinitionDetailPage = lazyNamed(
  () => import("./definitions/definition-detail-page"),
  "DefinitionDetailPage",
);
const NewDefinitionPage = lazyNamed(
  () => import("./definitions/definition-detail-page"),
  "NewDefinitionPage",
);
// The Memories page stays until the Definitions tab is on everywhere. It keeps
// the Memories name rather than the definitions naming used elsewhere.
const MemoriesSettings = lazyNamed(
  () => import("./memories"),
  "MemoriesSettings",
);
const GuardrailsPage = lazyNamed(
  () => import("src/pages/context-hub/guardrails"),
  "GuardrailsPage",
);
const AdsGuardrailsPage = lazyNamed(
  () => import("src/pages/context-hub/guardrails/ads"),
  "AdsGuardrailsPage",
);
const EmailGuardrailsPage = lazyNamed(
  () => import("src/pages/context-hub/guardrails/email"),
  "EmailGuardrails",
);
const AgentReviewersPage = lazyNamed(
  () => import("src/pages/context-hub/guardrails/reviewers"),
  "AgentReviewersPage",
);
const PersonalizationDataPage = lazyNamed(
  () => import("./personalization-data"),
  "PersonalizationDataPage",
);
const AddContextFromSource = lazyNamed(
  () => import("./external-context-sources/add-from-source"),
  "AddContextFromSource",
);
const EditSemanticLayerContextSource = lazyNamed(
  () => import("./external-context-sources/edit-semantic-layer-context-source"),
  "EditSemanticLayerContextSource",
);
const CreativeDimensionsSettings = lazyNamed(
  () => import("./creative-dimensions"),
  "CreativeDimensionsSettings",
);
const TagDetailPage = lazyNamed(
  () => import("./creative-dimensions/tag-detail-page"),
  "TagDetailPage",
);
const NewTagPage = lazyNamed(
  () => import("./creative-dimensions/tag-detail-page"),
  "NewTagPage",
);
const StylesListPage = lazyNamed(() => import("./styles"), "StylesListPage");
const ContextHubAssets = lazyNamed(
  () => import("./assets"),
  "ContextHubAssets",
);
const AssetLibraryLayout = lazyNamed(
  () => import("src/pages/ad-studio/assets"),
  "AssetLibraryLayout",
);
const AssetsBrowse = lazyNamed(
  () => import("src/pages/ad-studio/assets"),
  "AssetsBrowse",
);
const StyleDetailPage = lazyNamed(
  () => import("./styles/detail"),
  "StyleDetailPage",
);
const NewStylePage = lazyNamed(
  () => import("./styles/detail/new-style"),
  "NewStylePage",
);
const IntegrationDrawer = lazyNamed(
  () => import("./integrations/components"),
  "IntegrationDrawer",
);

const ContextHubFeatureGate = ({ children }: { children: ReactNode }) => {
  if (!useIsContextHubEnabled()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AdsOrLifecycleFeatureGate = ({ children }: { children: ReactNode }) => {
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();

  if (!isAdStudioEnabled && !isLifecycleStudioEnabled) {
    return <Navigate to="/context-hub/knowledge" replace />;
  }

  return <>{children}</>;
};

const LifecycleStudioSettingsGate = ({
  children,
  redirect = "/context-hub/knowledge/files",
}: {
  children: ReactNode;
  redirect?: string;
}) => {
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();

  if (!isLifecycleStudioEnabled) {
    return <Navigate to={redirect} replace />;
  }

  return <>{children}</>;
};

const ExternalContextGate = ({ children }: { children: ReactNode }) => {
  const isExternalContextEnabled = useExternalContextEnabled();

  if (!isExternalContextEnabled) {
    return <Navigate to="/context-hub/connected-data/integrations" replace />;
  }

  return <>{children}</>;
};

const AdStudioSettingsGate = ({ children }: { children: ReactNode }) => {
  const isAdStudioEnabled = useIsAdStudioEnabled();

  if (!isAdStudioEnabled) {
    return <Navigate to="/context-hub/knowledge/files" replace />;
  }

  return <>{children}</>;
};

// Both surfaces ship while the flag rolls out, so the retired path forwards to
// the Definitions tab only once that tab exists for this workspace.
const MemoriesRoute = () => {
  const isDefinitionsTabEnabled = useDefinitionsTabEnabled();

  if (isDefinitionsTabEnabled) {
    return <Navigate to={DEFINITIONS_PATH} replace />;
  }

  return <MemoriesSettings />;
};

const DefinitionsRoute = () => {
  const isKnowledgeRefreshEnabled = useDefinitionsKnowledgeRefreshEnabled();

  return isKnowledgeRefreshEnabled ? (
    <DefinitionsListPage />
  ) : (
    <DefinitionsPage />
  );
};

const DefinitionPaneRedirect = () => {
  const { definitionId } = useParams<{ definitionId: string }>();

  return (
    <Navigate
      to={definitionId ? definitionPanePath(definitionId) : DEFINITIONS_PATH}
      replace
    />
  );
};

const DefinitionDetailRoute = () => {
  const isKnowledgeRefreshEnabled = useDefinitionsKnowledgeRefreshEnabled();

  return isKnowledgeRefreshEnabled ? (
    <DefinitionDetailPage />
  ) : (
    <DefinitionPaneRedirect />
  );
};

const NewDefinitionRoute = () => {
  const isKnowledgeRefreshEnabled = useDefinitionsKnowledgeRefreshEnabled();

  if (isKnowledgeRefreshEnabled) return <NewDefinitionPage />;

  return <Navigate to={DEFINITIONS_PATH} replace />;
};

// Keeps the query and hash on retired paths, so deep links keep their params.
const RedirectPreservingSearch = ({ to }: { to: string }) => {
  const location = useLocation();

  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
};

const LegacyIntegrationDrawerRedirect = () => {
  const { type } = useParams<{ type: string }>();

  return (
    <RedirectPreservingSearch
      to={`/context-hub/connected-data/integrations/extensions/${type}`}
    />
  );
};

const LegacyEditExternalContextSourceRedirect = () => {
  const { id } = useParams<{ id: string }>();

  return (
    <RedirectPreservingSearch
      to={`/context-hub/connected-data/integrations/external-context-sources/${id}/edit`}
    />
  );
};

const LegacyAssetCollectionRedirect = () => {
  const { collectionId } = useParams<{ collectionId: string }>();

  return (
    <RedirectPreservingSearch to={`${ASSET_LIBRARY_PATH}/${collectionId}`} />
  );
};

const LegacyStyleDetailRedirect = () => {
  const { styleId } = useParams<{ styleId: string }>();

  return (
    <RedirectPreservingSearch to={`/context-hub/knowledge/styles/${styleId}`} />
  );
};

// Agent-written briefs link products as `/context-hub/catalog/<id>`.
const LegacyCatalogRedirect = () => {
  const { "*": rest } = useParams();

  return (
    <RedirectPreservingSearch
      to={rest ? `${CATALOG_PATH}/${rest}` : CATALOG_PATH}
    />
  );
};

const ContextHubIndexRedirect = () => {
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();

  if (isAdStudioEnabled || isLifecycleStudioEnabled) {
    return <Navigate to="brands" replace />;
  }

  return <Navigate to="knowledge" replace />;
};

// Serves bookmarks and agent-written links to `/context-hub/context/*`. A doc
// or skill's `?contextId=<id>` becomes the `files/<id>` or `skills/<id>` path.
const LegacyContextRedirect = () => {
  const { "*": rest = "" } = useParams();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const legacyContextId = search.get("contextId");
  const [first, ...others] = rest.split("/").filter(Boolean);
  const tab = first === "entities" ? "files" : first;
  let segments = tab ? [tab, ...others] : [];

  if (
    legacyContextId &&
    others.length === 0 &&
    (tab === undefined || tab === "files" || tab === "skills")
  ) {
    segments = [tab ?? "files", encodeURIComponent(legacyContextId)];
    search.delete("contextId");
  }

  const query = search.toString();

  return (
    <Navigate
      to={{
        pathname: ["/context-hub/knowledge", ...segments].join("/"),
        search: query ? `?${query}` : "",
        hash: location.hash,
      }}
      state={location.state}
      replace
    />
  );
};

export const routes: RouteObject[] = [
  {
    path: "context-hub",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={{
          v2: { resource: "workspace", grant: "can_configure_agents" },
        }}
        redirect="/"
      >
        <ContextHubFeatureGate>
          <ContextHubLayout />
        </ContextHubFeatureGate>
      </PermissionedRoute>
    ),
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <ContextHubIndexRedirect />,
      },
      {
        path: "brands",
        spacesAccess: "hidden-from-limited-view",
        element: <ContextHubBrands />,
      },
      {
        path: `brands/${BRAND_SETTINGS_SEGMENT}`,
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="contextHubNdBrandSettings"
            redirect={BRANDS_PATH}
          >
            <ContextHubBrands tab="brand-settings" />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "guardrails",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <GuardrailsPage />
          </AdsOrLifecycleFeatureGate>
        ),
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <GuardrailsIndexRedirect />,
          },
          {
            path: "ads",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <AdStudioSettingsGate>
                <AdsGuardrailsPage />
              </AdStudioSettingsGate>
            ),
          },
          {
            path: "emails",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <LifecycleStudioSettingsGate>
                <EmailGuardrailsPage />
              </LifecycleStudioSettingsGate>
            ),
          },
          {
            path: "agent-reviewers",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <AdStudioSettingsGate>
                <AgentReviewersPage />
              </AdStudioSettingsGate>
            ),
          },
        ],
      },
      {
        path: "styles",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <RedirectPreservingSearch to="/context-hub/knowledge/styles" />
        ),
      },
      {
        path: "styles/:styleId",
        spacesAccess: "hidden-from-limited-view",
        element: <LegacyStyleDetailRedirect />,
      },
      {
        path: "catalog/*",
        spacesAccess: "hidden-from-limited-view",
        element: <LegacyCatalogRedirect />,
      },
      {
        path: "assets",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <ContextHubAssets />
          </AdsOrLifecycleFeatureGate>
        ),
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <RedirectPreservingSearch to={ASSET_LIBRARY_PATH} />,
          },
          {
            path: "library",
            spacesAccess: "hidden-from-limited-view",
            element: (
              // The asset library filters by brand through the Ad Studio providers.
              <StickySearchParamsProvider>
                <AdStudioBrandProfileFilterProvider>
                  <AssetLibraryLayout />
                </AdStudioBrandProfileFilterProvider>
              </StickySearchParamsProvider>
            ),
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view",
                element: <AssetsBrowse />,
              },
              {
                path: ":collectionId",
                spacesAccess: "hidden-from-limited-view",
                element: <AssetsBrowse />,
              },
            ],
          },
          // Product catalogs are collections in the library now.
          {
            path: "catalog",
            spacesAccess: "hidden-from-limited-view",
            element: <CatalogIndexRedirect />,
          },
          {
            path: "catalog/feeds",
            spacesAccess: "hidden-from-limited-view",
            element: <CatalogIndexRedirect />,
          },
          {
            path: "catalog/:catalogItemId",
            spacesAccess: "hidden-from-limited-view",
            element: <CatalogProductRedirect />,
          },
          {
            path: ":collectionId",
            spacesAccess: "hidden-from-limited-view",
            element: <LegacyAssetCollectionRedirect />,
          },
        ],
      },
      {
        // Nested under the tab's URL, but registered as a sibling of the
        // `knowledge` layout route rather than a child: the detail page brings its
        // own page shell, and rendering it in the tabs' outlet would card-in-card.
        path: "knowledge/copy-references/:channel",
        spacesAccess: "hidden-from-limited-view",
        element: <CopyReferencesDetailPage />,
      },
      {
        // Siblings of the `knowledge` layout for the same reason as copy-references detail.
        path: "knowledge/files/new",
        spacesAccess: "hidden-from-limited-view",
        element: <NewFilePage />,
      },
      {
        path: "knowledge/files/:contextId",
        spacesAccess: "hidden-from-limited-view",
        element: <FileDetailPage />,
      },
      {
        path: "knowledge/definitions/new",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="agentDefinitionsReviewTab"
            redirect={MEMORIES_PATH}
          >
            <NewDefinitionRoute />
          </FeatureFlagRoute>
        ),
      },
      {
        // Ranks ahead of the Knowledge layout's list route, so with the flag off links to a definition open the original page's side pane.
        path: "knowledge/definitions/:definitionId",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="agentDefinitionsReviewTab"
            redirect={MEMORIES_PATH}
          >
            <DefinitionDetailRoute />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "knowledge/skills/new",
        spacesAccess: "hidden-from-limited-view",
        element: <NewSkillPage />,
      },
      {
        path: "knowledge/skills/:contextId",
        spacesAccess: "hidden-from-limited-view",
        element: <SkillDetailPage />,
      },
      {
        path: "knowledge/skills/new/builder",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute flagKey="ampDagSkills">
            <DagBuilderPage />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "knowledge/skills/:id/builder",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute flagKey="ampDagSkills">
            <DagBuilderPage />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "knowledge/styles/new",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <NewStylePage />
          </AdsOrLifecycleFeatureGate>
        ),
      },
      {
        path: "knowledge/styles/:styleId",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <StyleDetailPage />
          </AdsOrLifecycleFeatureGate>
        ),
      },
      {
        path: "knowledge/tags/new",
        spacesAccess: "hidden-from-limited-view",
        element: <NewTagPage />,
      },
      {
        path: "knowledge/tags/:tagKey",
        spacesAccess: "hidden-from-limited-view",
        element: <TagDetailPage />,
      },
      {
        path: "context/personalization-data",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <RedirectPreservingSearch to="/context-hub/connected-data/personalization-data" />
        ),
      },
      {
        path: "context/external-context-sources",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <RedirectPreservingSearch to="/context-hub/connected-data/integrations" />
        ),
      },
      {
        path: "context/external-context-sources/new",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <RedirectPreservingSearch to="/context-hub/connected-data/integrations/external-context-sources/new" />
        ),
      },
      {
        path: "context/external-context-sources/:id/edit",
        spacesAccess: "hidden-from-limited-view",
        element: <LegacyEditExternalContextSourceRedirect />,
      },
      {
        // Retired demo tab; keeps old bookmarks off a blank page.
        path: "context/alignment/*",
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="/context-hub/knowledge/files" replace />,
      },
      {
        path: "context/guardrails",
        spacesAccess: "hidden-from-limited-view",
        element: <GuardrailsIndexRedirect legacy />,
      },
      {
        path: "context/best-practices",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <LifecycleStudioSettingsGate>
            <GuardrailsIndexRedirect legacy />
          </LifecycleStudioSettingsGate>
        ),
      },
      {
        path: "context/signals",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdStudioSettingsGate>
            <Navigate to={insightsSettingsPath("sources")} replace />
          </AdStudioSettingsGate>
        ),
      },
      {
        path: "context/opportunities",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <OpportunitiesFeatureRoute redirect="/context-hub/knowledge/files">
            <AdStudioSettingsGate>
              <Navigate to={insightsSettingsPath("opportunities")} replace />
            </AdStudioSettingsGate>
          </OpportunitiesFeatureRoute>
        ),
      },
      {
        // The explicit `context/...` redirects above outrank this splat.
        path: "context/*",
        spacesAccess: "hidden-from-limited-view",
        element: <LegacyContextRedirect />,
      },
      {
        path: "knowledge",
        spacesAccess: "hidden-from-limited-view",
        element: <ContextHubContext />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="files" replace />,
          },
          {
            path: "files",
            spacesAccess: "hidden-from-limited-view",
            element: <ContextEntitiesSettings />,
          },
          {
            path: "styles",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <AdsOrLifecycleFeatureGate>
                <StylesListPage />
              </AdsOrLifecycleFeatureGate>
            ),
          },
          {
            path: "skills",
            spacesAccess: "hidden-from-limited-view",
            element: <SkillsSettings />,
          },
          {
            path: "definitions",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <FeatureFlagRoute
                flagKey="agentDefinitionsReviewTab"
                redirect={MEMORIES_PATH}
              >
                <DefinitionsRoute />
              </FeatureFlagRoute>
            ),
          },
          {
            path: "memories",
            spacesAccess: "hidden-from-limited-view",
            element: <MemoriesRoute />,
          },
          {
            path: "copy-references",
            spacesAccess: "hidden-from-limited-view",
            element: <CopyReferencesPage />,
          },
          {
            path: "tags",
            spacesAccess: "hidden-from-limited-view",
            element: <CreativeDimensionsSettings />,
          },
        ],
      },
      {
        path: "connected-data",
        spacesAccess: "hidden-from-limited-view",
        element: <ContextHubConnectedData />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: (
              <RedirectPreservingSearch to="/context-hub/connected-data/integrations" />
            ),
          },
          {
            path: "integrations",
            spacesAccess: "hidden-from-limited-view",
            element: <ConnectedDataIntegrations />,
            children: [
              {
                path: "extensions/:type",
                spacesAccess: "hidden-from-limited-view",
                element: <IntegrationDrawer />,
              },
              {
                // The wizards are full-screen drawers, so they mount over the
                // live Integrations page rather than replacing it.
                path: "external-context-sources/new",
                spacesAccess: "hidden-from-limited-view",
                element: (
                  <ExternalContextGate>
                    <AddContextFromSource />
                  </ExternalContextGate>
                ),
              },
              {
                path: "external-context-sources/:id/edit",
                spacesAccess: "hidden-from-limited-view",
                element: (
                  <ExternalContextGate>
                    <EditSemanticLayerContextSource />
                  </ExternalContextGate>
                ),
              },
            ],
          },
          {
            path: "personalization-data",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <LifecycleStudioSettingsGate redirect="/context-hub/connected-data/integrations">
                <PersonalizationDataPage />
              </LifecycleStudioSettingsGate>
            ),
          },
        ],
      },
      {
        path: "connected-data/connections",
        spacesAccess: "hidden-from-limited-view",
        element: <ConnectionsListPage />,
      },
      {
        path: "connected-data/connections/new",
        spacesAccess: "hidden-from-limited-view",
        element: <ConnectionFlowPage />,
      },
      {
        path: "connected-data/connections/:syncId",
        spacesAccess: "hidden-from-limited-view",
        element: <ConnectionStatusPage />,
      },
      {
        path: "connected-data/connections/:syncId/edit",
        spacesAccess: "hidden-from-limited-view",
        element: <ConnectionFlowPage />,
      },
      {
        path: "connections",
        spacesAccess: "hidden-from-limited-view",
        element: <RedirectPreservingSearch to={CONNECTIONS_PATH} />,
      },
      {
        path: "connections/integrations/:type",
        spacesAccess: "hidden-from-limited-view",
        element: <LegacyIntegrationDrawerRedirect />,
      },
    ],
  },
];
